import { useQuery } from "@apollo/client/react";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  ListCategorySchemaMigrationsDocument,
  type ListCategorySchemaMigrationsQuery,
  type ListCategorySchemaMigrationsQueryVariables,
} from "~/features/admin/categories/gqls";
import { useTenant } from "~/features/tenant/context";

const PAGE_SIZE = 10;

export function useCategorySchemaMigrations(categoryId: string) {
  const { tenantId } = useTenant();
  if (!tenantId) throw new Error("Tenant ID is required");
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const requestKey = `${tenantId}:${categoryId}`;
  const requestGeneration = useRef({ key: requestKey, value: 0 });
  if (requestGeneration.current.key !== requestKey) {
    requestGeneration.current = { key: requestKey, value: requestGeneration.current.value + 1 };
  }

  useEffect(() => {
    setLoadingMore(false);
    setLoadMoreError(false);
  }, [tenantId, categoryId]);

  const query = useQuery<ListCategorySchemaMigrationsQuery, ListCategorySchemaMigrationsQueryVariables>(
    ListCategorySchemaMigrationsDocument,
    {
      variables: { tenantId, categoryId, limit: PAGE_SIZE, offset: 0 },
    },
  );
  const output = query.data?.cnutListCategorySchemaMigrations;
  const refresh = useCallback(() => query.refetch(), [query.refetch]);

  const loadMore = async () => {
    if (!output?.hasNextPage || loadingMore) return;
    const requestGenerationAtStart = requestGeneration.current.value;

    setLoadingMore(true);
    setLoadMoreError(false);
    try {
      await query.fetchMore({
        variables: {
          tenantId,
          categoryId,
          limit: PAGE_SIZE,
          offset: output.migrations.length,
        },
        updateQuery: (previous, { fetchMoreResult }) => {
          if (requestGeneration.current.value !== requestGenerationAtStart) return previous;
          return {
            ...previous,
            cnutListCategorySchemaMigrations: {
              ...fetchMoreResult.cnutListCategorySchemaMigrations,
              migrations: [
                ...previous.cnutListCategorySchemaMigrations.migrations,
                ...fetchMoreResult.cnutListCategorySchemaMigrations.migrations,
              ],
            },
          };
        },
      });
    } catch {
      if (requestGeneration.current.value === requestGenerationAtStart) {
        setLoadMoreError(true);
      }
    } finally {
      if (requestGeneration.current.value === requestGenerationAtStart) {
        setLoadingMore(false);
      }
    }
  };

  return {
    migrations: output?.migrations ?? [],
    hasNextPage: output?.hasNextPage ?? false,
    loading: query.loading,
    error: query.error != null || loadMoreError,
    loadingMore,
    loadMore,
    refresh,
  };
}
